import { useCallback, useEffect, useState } from 'react';
import { ActionIcon, Box, Group, Stack, Text } from '@mantine/core';
import { useLocation, useNavigate } from 'react-router-dom';
import { Team } from '@/common/types/databaseTypes';
import { TeamTabValue, TEAM_TABS } from '../types';
import { useTeamStore } from '@/stores/teamStore';
import UserAvatar from '@/common/components/UserAvatars/UserAvatar';
import { IconSettings } from '@tabler/icons-react';
import { useMantineTheme } from '@mantine/core';
import { useTeamLastTab } from '../hooks/useTeamLastTab';
import { useAuthStore } from '@/stores/authStore';
import { useSecondarySidebarStore } from '@/common/stores/secondarySidebarStore';

export const TeamItem = ({ team }: { team: Team }) => {
    const theme = useMantineTheme();
    const { setCurrentTeam, currentTeam } = useTeamStore();
    const { user } = useAuthStore();
    const navigate = useNavigate();
    const location = useLocation();

    // SecondarySidebar 접기/펴기 상태 관리
    const { isExpanded } = useSecondarySidebarStore();

    // 팀별 마지막 방문 탭 관리
    const { saveLastTab, getLastTab } = useTeamLastTab(user?.id, team.id);

    const getActiveTabFromPath = useCallback((): TeamTabValue => {
        const path = location.pathname;
        if (path.endsWith('/goal')) return TEAM_TABS.GOAL;
        if (path.endsWith('/project')) return TEAM_TABS.PROJECT;
        if (path.includes('/member')) return TEAM_TABS.MEMBER;
        if (path.includes('/setting')) return TEAM_TABS.TEAM_SETTING;
        return null;
    }, [location.pathname]);

    const [activeTab, setActiveTab] = useState<TeamTabValue>(() => {
        // 현재 팀인 경우 URL 기반으로, 아닌 경우 마지막 방문 탭으로 설정
        if (currentTeam?.id === team.id) {
            return getActiveTabFromPath();
        }
        return null;
    });
    const [isHovered, setIsHovered] = useState<boolean>(false);

    useEffect(() => {
        if (currentTeam?.id === team.id) {
            // 현재 팀인 경우 URL 기반으로 탭 설정
            const currentTab = getActiveTabFromPath();
            setActiveTab(currentTab);

            // 유효한 탭인 경우 마지막 방문 탭으로 저장
            saveLastTab(currentTab);
        } else {
            setActiveTab(null);
        }
    }, [
        location.pathname,
        currentTeam?.id,
        team.id,
        getActiveTabFromPath,
        saveLastTab,
        getLastTab,
    ]);

    const handleTeamClick = async (tab?: TeamTabValue) => {
        // 마지막 방문 탭으로 이동
        const targetTab = tab || getLastTab();

        if (!targetTab) return;

        const routes: Record<Exclude<TeamTabValue, null>, string> = {
            [TEAM_TABS.TEAM_SETTING]: `/team/${team.id}/setting`,
            [TEAM_TABS.GOAL]: `/team/${team.id}/goal`,
            [TEAM_TABS.PROJECT]: `/team/${team.id}/project`,
            [TEAM_TABS.MEMBER]: `/team/${team.id}/member`,
        };

        if (routes[targetTab]) {
            await navigate(routes[targetTab]);
            await setCurrentTeam(team);
            setActiveTab(targetTab);

            // 새로운 탭으로 이동할 때 마지막 방문 탭으로 저장
            saveLastTab(targetTab);
        }
    };

    return (
        <Stack key={team.id} gap={0}>
            <Box
                onClick={() => void handleTeamClick()}
                c="dimmed"
                p={8}
                bg={
                    currentTeam?.id === team.id
                        ? 'blue.0'
                        : isHovered
                          ? '#868E961a'
                          : 'transparent'
                }
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={{
                    cursor: 'pointer',
                    borderRadius: '8px',
                }}
            >
                <Group
                    justify={isExpanded ? 'start' : 'center'}
                    gap={8}
                    c={currentTeam?.id === team.id ? 'blue.6' : '#212529'}
                >
                    <UserAvatar
                        name={team.name}
                        imageUrl={team.profile_image_url}
                        size={18}
                    />
                    {isExpanded && (
                        <Text
                            size="xs"
                            fw={currentTeam?.id === team.id ? 600 : 500}
                            flex={1}
                            lineClamp={1}
                        >
                            {team.name}
                        </Text>
                    )}

                    {isHovered && isExpanded && (
                        <ActionIcon
                            size={12}
                            variant="transparent"
                            onClick={e => {
                                e.stopPropagation();
                                void handleTeamClick(TEAM_TABS.TEAM_SETTING);
                            }}
                        >
                            <IconSettings
                                size={14}
                                color={theme.colors.gray[6]}
                            />
                        </ActionIcon>
                    )}
                </Group>
            </Box>
        </Stack>
    );
};
